import { mediaQueryLarge, mediaQueryHeader } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  /** Scrolls the filter drawer so the bottom of the opened accordion aligns with the bottom of the scrollable area. */
  #handleToggle = () => {
    if (!this.details.open) return;

    const scrollContainer = this.closest('.facets-drawer__filters');
    if (!scrollContainer) return;

    const scroll = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const detailsRect = this.getBoundingClientRect();
      if (detailsRect.bottom <= containerRect.bottom) return;
      if (detailsRect.top < containerRect.top) return;
      const delta = detailsRect.bottom - containerRect.bottom;
      const maxDelta = detailsRect.top - containerRect.top;
      scrollContainer.scrollBy({ top: Math.min(delta, maxDelta), behavior: 'smooth' });
    };

    // Wait for the block-size transition on the details content to finish
    // before measuring, so we get the fully-expanded height.
    const content = this.querySelector('floating-panel-component, .details-content');
    if (!content) {
      requestAnimationFrame(scroll);
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      scroll();
    };

    content.addEventListener('transitionend', finish, { once: true });

    // Fallback: if transition is disabled (e.g. no :focus-within), fire immediately
    setTimeout(finish, 400);
  };

  /** Drawer accordions use the 1200px header breakpoint, all others use 750px */
  get #activeMediaQuery() {
    return this.closest('.menu-drawer') ? mediaQueryHeader : mediaQueryLarge;
  }

  get #isMobile() {
    return !this.#activeMediaQuery.matches;
  }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.closest('.menu-drawer__column--sticky-footer')) {
      this.setAttribute('data-disable-on-mobile', 'true');
      this.setAttribute('open-by-default-on-mobile', '');
    }

    const submenu = this.closest('.menu-drawer__submenu');
    if (submenu && submenu.querySelector('accordion-custom') === this) {
      // Only auto-open if this accordion lives inside the very first
      // .menu-component in the submenu (i.e. the first block IS an accordion).
      const firstComponent = submenu.querySelector('.menu-component');
      if (firstComponent?.contains(this)) {
        this.setAttribute('open-by-default-on-mobile', '');
      }
    }

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.#activeMediaQuery.addEventListener('change', this.#handleMediaQueryChange, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = this.#isMobile;
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = this.#isMobile;

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
